import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardCheck,
  Presentation,
  Lightbulb,
  ListChecks,
  MessageSquareText,
  Rocket,
  ShieldAlert,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import api from "../lib/api";
import { useToasts } from "../components/Toasts";
import "./VentureStudioPage.css";

const FLOW = [
  ["Idea", Lightbulb, "purple"],
  ["AI Interview", MessageSquareText, "blue"],
  ["Assessment", ClipboardCheck, "cyan"],
  ["Business Case", BriefcaseBusiness, "teal"],
  ["Pitch Deck", Presentation, "amber"],
  ["Validate", ListChecks, "green"],
  ["Build Mission", Rocket, "violet"],
];

function SectionCard({ title, Icon, tone = "blue", children }) {
  return (
    <section className={`vs-card tone-${tone}`}>
      <header><span><Icon size={17} /></span><h3>{title}</h3></header>
      <div>{children}</div>
    </section>
  );
}

export default function VentureStudioPage() {
  const navigate = useNavigate();
  const toast = useToasts((state) => state.push);
  const [form, setForm] = useState({ idea: "", problem: "", audience: "", evidence: "", constraints: "", desired_outcome: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const assess = async (event) => {
    event.preventDefault();
    if (!form.idea.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/venture-studio/assess", Object.fromEntries(
        Object.entries(form).filter(([, value]) => value?.trim())
      ));
      setResult(data);
      try {
        const { data: ps } = await api.get("/projects");
        setProjects(ps);
        if (ps.length && !projectId) setProjectId(ps[0].id);
      } catch { /* mission handoff remains optional */ }
      toast("Venture assessment ready.", "ok");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Venture assessment failed.", "err");
    } finally { setBusy(false); }
  };

  const createMission = async () => {
    if (!result || creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/missions", {
        title: result.mission_title,
        brief: result.mission_brief,
        priority: "normal",
        project_id: projectId || undefined,
      });
      navigate(`/commandlinc/missions/${data.id}`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Mission handoff failed.", "err");
    } finally { setCreating(false); }
  };

  const scoreTone = useMemo(() => {
    const score = result?.opportunity_score ?? 0;
    if (score >= 75) return "green";
    if (score >= 55) return "cyan";
    if (score >= 35) return "amber";
    return "red";
  }, [result]);

  return (
    <div className="vs-page">
      <header className="vs-hero">
        <div>
          <small>COMMANDLINC · VENTURE STUDIO</small>
          <h1>From rough idea to decision-ready business case.</h1>
          <p>Use AI to challenge the idea, structure the interview, build the case, outline the pitch and hand a validated brief directly into CommandLinc.</p>
        </div>
        <button className="vs-close" onClick={() => navigate("/commandlinc")} title="Back to CommandLinc"><X size={17} /></button>
      </header>

      <section className="vs-flow" aria-label="Venture lifecycle">
        {FLOW.map(([label, Icon, tone], index) => (
          <div className={`vs-flow-step tone-${tone}`} key={label}>
            <span><Icon size={17} /></span><b>{label}</b>{index < FLOW.length - 1 ? <ArrowRight size={14} /> : null}
          </div>
        ))}
      </section>

      {!result ? (
        <form className="vs-intake" onSubmit={assess}>
          <div className="vs-intake-main">
            <label><small>Your idea *</small><textarea autoFocus rows={5} value={form.idea} onChange={(e) => set("idea", e.target.value)} placeholder="Describe the idea in your own words. It can be rough." /></label>
            <div className="vs-intake-grid">
              <label><small>Problem / frustration</small><textarea rows={3} value={form.problem} onChange={(e) => set("problem", e.target.value)} placeholder="What problem are you trying to solve?" /></label>
              <label><small>Who is it for?</small><textarea rows={3} value={form.audience} onChange={(e) => set("audience", e.target.value)} placeholder="Who would use, buy, sponsor or benefit from it?" /></label>
              <label><small>Evidence you already have</small><textarea rows={3} value={form.evidence} onChange={(e) => set("evidence", e.target.value)} placeholder="Feedback, demand, prototypes, experience, conversations..." /></label>
              <label><small>Constraints</small><textarea rows={3} value={form.constraints} onChange={(e) => set("constraints", e.target.value)} placeholder="Time, budget, skills, regulation, dependencies..." /></label>
            </div>
            <label><small>Desired outcome</small><input value={form.desired_outcome} onChange={(e) => set("desired_outcome", e.target.value)} placeholder="What would success look like?" /></label>
          </div>
          <aside className="vs-intake-side">
            <Sparkles size={22} />
            <h3>AI-assisted assessment</h3>
            <p>The system will challenge assumptions, not just validate the idea. Unsupported market claims stay labeled as unknowns.</p>
            <button className="btn primary" disabled={!form.idea.trim() || busy}>{busy ? "Assessing…" : "Build assessment"}</button>
          </aside>
        </form>
      ) : (
        <div className="vs-results">
          <section className={`vs-score-card tone-${scoreTone}`}>
            <div><small>OPPORTUNITY SCORE</small><strong>{result.opportunity_score}</strong><span>/100</span></div>
            <div><h2>{result.venture_name}</h2><p>{result.executive_summary}</p><small>{result.score_rationale}</small></div>
          </section>

          <div className="vs-grid">
            <SectionCard title="AI interview" Icon={MessageSquareText} tone="blue">
              <ol>{result.interview_questions?.map((q) => <li key={q}>{q}</li>)}</ol>
            </SectionCard>
            <SectionCard title="Business case" Icon={BriefcaseBusiness} tone="teal">
              <p><b>Problem:</b> {result.business_case?.problem}</p>
              <p><b>Customer:</b> {result.business_case?.customer}</p>
              <p><b>Value:</b> {result.business_case?.value_proposition}</p>
              <h4>Business model options</h4><ul>{result.business_case?.business_model?.map((x) => <li key={x}>{x}</li>)}</ul>
              <h4>Key assumptions</h4><ul>{result.business_case?.key_assumptions?.map((x) => <li key={x}>{x}</li>)}</ul>
            </SectionCard>
            <SectionCard title="Pitch deck" Icon={Presentation} tone="amber">
              <div className="vs-slide-list">{result.pitch_deck?.map((slide, i) => <article key={`${slide.title}-${i}`}><span>{i + 1}</span><div><b>{slide.title}</b><small>{slide.purpose}</small></div></article>)}</div>
            </SectionCard>
            <SectionCard title="Validation plan" Icon={ListChecks} tone="green">
              <ol>{result.validation_plan?.map((x) => <li key={x}>{x}</li>)}</ol>
              <h4>Success metrics</h4><ul>{result.business_case?.success_metrics?.map((x) => <li key={x}>{x}</li>)}</ul>
            </SectionCard>
            <SectionCard title="Risks & mitigations" Icon={ShieldAlert} tone="red">
              {result.risks?.map((r) => <article className="vs-risk" key={r.risk}><b>{r.risk}</b><small>{r.mitigation}</small></article>)}
            </SectionCard>
            <SectionCard title="Recommended next step" Icon={Target} tone="purple">
              <p>{result.recommended_next_step}</p>
              <div className="vs-mission-handoff">
                <small>MISSION HANDOFF</small><b>{result.mission_title}</b><p>{result.mission_brief}</p>
                {projects.length ? <label><small>Project / business</small><select value={projectId} onChange={(e) => setProjectId(e.target.value)}>{projects.map((p) => <option value={p.id} key={p.id}>{p.name}</option>)}</select></label> : null}
                <button className="btn primary" onClick={createMission} disabled={creating}>{creating ? "Creating…" : <><Rocket size={14} /> Create CommandLinc mission</>}</button>
              </div>
            </SectionCard>
          </div>

          <div className="vs-result-actions">
            <button className="btn" onClick={() => setResult(null)}>Revise intake</button>
            <button className="btn" onClick={() => navigate("/commandlinc")}><CheckCircle2 size={14} /> Back to lifecycle map</button>
          </div>
        </div>
      )}
    </div>
  );
}
